import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Text,
  Button,
  Flex,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { CalenderIcon } from "@root/assets/icons";
import { useCourseManagement } from "@root/hooks/useCourseManagement";
import { REQUIREMENT_TYPE } from "@root/constants";
import {
  formatDateToISO,
  formatYear,
  formatDuration,
  getChapterSummary,
} from "@root/utils/date-helper";
import { VideoNewIcon } from "@root/assets/icons/VideoNewIcon";
import { CompletedIcon } from "@root/assets/icons/CompletedIcon";
import ReactVideoPlayerNew from "../Courses/ReactVideoPlayerNew";
import { useCourseDetailTourGuide } from "@hooks/useCourseDetailTourGuide";
import { TourGuide, RestartTourButton } from "@components/tour-guide";

const FreeCourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const [enrolled, setEnrolled] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(0);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);
  const [selectedVideoId, setSelectedVideoId] = useState<number | null>(null);

  // Tour guide integration
  const { courseDetailTourGuide, enrollmentPeriodRef, chaptersHeaderRef } =
    useCourseDetailTourGuide();
  const {
    courseChaptersData,
    getCourseChapters,
    freeCourseData: rawCourseData,
    getCoursesLoading,
    courseProgressData,
    fetchFreeCourse,
  } = useCourseManagement();
  const courseData: any = rawCourseData;
  useEffect(() => {
    if (id) {
      fetchFreeCourse(Number(id));
      getCourseChapters(Number(id), { isSignedUrl: true });
    }
  }, [id]);

  // Auto-enroll for free courses - no enrollment screen needed
  useEffect(() => {
    if (courseData) {
      setEnrolled(true);
    }
  }, [courseData]);

  if (getCoursesLoading) {
    return (
      <Center minH="100vh" width="100%">
        <Flex flexDirection="column" alignItems="center" gap={4}>
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="#1819B4"
            size="xl"
          />
          <Text fontSize="16px" color="#5C6670">
            {t("loading")}
          </Text>
        </Flex>
      </Center>
    );
  }
  if (!courseData) {
    return (
      <Center minH="100vh" width="100%">
        <Text fontSize="18px" color="#5C6670">
          {t("courseDetail.notFound")}
        </Text>
      </Center>
    );
  }
  const chapters = (courseChaptersData as any)?.rows || [];

  const progressLookup: { [key: number]: boolean } = {};
  courseProgressData?.[0]?.forEach((item: any) => {
    if (item?.video && item?.video?.id) {
      progressLookup[item?.video?.id] = item?.completed === true;
    }
  });

  const currentVideoUrl =
    enrolled && chapters[selectedChapter]?.courseVideos?.[0]?.url
      ? chapters[selectedChapter]?.courseVideos?.[0]?.url
      : null;

  const requirementLabel =
    courseData.requirement === REQUIREMENT_TYPE.REQUIRED
      ? t("coursesPage.essentialRequired")
      : courseData.requirement === REQUIREMENT_TYPE.OPTIONAL
        ? t("coursesPage.optionalRecommended")
        : null;

  const renderVideoPlayer = () => {
    let selectedVideo: any = null;
    for (const chapter of (courseChaptersData as any)?.rows || []) {
      const video = chapter?.courseVideos?.find(
        (v: any) => v?.id === selectedVideoId,
      );
      if (video) {
        selectedVideo = video;
        break;
      }
    }
    if (selectedVideo && selectedVideo.isFree === false) {
      return (
        <Flex align="center" justify="center" height="300px" bg="#F5F5F5">
          <Box
            width="64px"
            height="64px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="8px"
            bg="#E0E0E0"
            fontSize="32px"
          >
            🔒
          </Box>
        </Flex>
      );
    }
    let videoUrl: string | null = null;
    if (selectedVideo) {
      videoUrl = selectedVideo?.url || selectedVideoUrl || currentVideoUrl;
    } else {
      videoUrl = selectedVideoUrl || currentVideoUrl;
    }

    return videoUrl ? (
      <ReactVideoPlayerNew
        key={videoUrl}
        videoUrl={videoUrl}
        title={courseData?.maintitle || courseData?.title}
        thumbnail={courseData?.thumbnail}
        onPlay={() => {}}
        onPause={() => {}}
        onEnded={() => {}}
      />
    ) : null;
  };

  const renderHeader = () => {
    return (
      <Box mb={6}>
        <Text fontWeight={700} fontSize="2xl" mb={1}>
          {courseData?.title}
        </Text>

        <Text color="gray.500" fontSize="sm" mb={4}>
          {courseData?.description}
        </Text>
        <Flex align="center" justify="space-between" flexWrap="wrap" mb={2}>
          <Flex align="center" gap={3}>
            <Text fontWeight={600} fontSize="14px" color={"#5C6670"}>
              {formatYear(courseData?.createdAt)}
            </Text>
            <Box w={2} h={2} borderRadius="full" bg="#009B9B" />
            <Text
              color="#0073E0"
              fontWeight={600}
              fontSize="14px"
              cursor="pointer"
              _hover={{ textDecoration: "underline" }}
            >
              {requirementLabel}
            </Text>
          </Flex>
        </Flex>
        <Flex
          ref={enrollmentPeriodRef}
          align="center"
          color="gray.500"
          fontSize="sm"
          mt={2}
          gap={2}
        >
          <CalenderIcon />

          <Text
            display={"flex"}
            alignItems={"center"}
            gap={2}
            fontSize={"14px"}
            fontWeight={400}
            color={"#5C6670"}
          >
            {t("courseDetail.enrollmentPeriod")}:{" "}
            <Text fontWeight={500} fontSize={"14px"} color={"#5C6670"}>
              {courseData?.enrollmentPeriodStart &&
                formatDateToISO(
                  new Date(courseData?.enrollmentPeriodStart),
                )?.replace(/-/g, ".")}{" "}
              ~{" "}
              {courseData?.enrollmentPeriodEnd &&
                formatDateToISO(
                  new Date(courseData?.enrollmentPeriodEnd),
                )?.replace(/-/g, ".")}
            </Text>
          </Text>
        </Flex>
      </Box>
    );
  };

  const renderChaptersVideo = (chapter: any, idx: number) => {
    return (
      <Box>
        {chapter?.courseVideos?.map((video: any, videoIdx: number) => {
          const progressItem = courseProgressData?.[0]?.find(
            (item: any) => item?.video?.id === video?.id,
          );
          const isCompleted = progressItem?.completed === true;
          const isFree = video?.isFree === true;
          return (
            <Flex
              key={video?.id || videoIdx}
              alignItems="center"
              py={3}
              px={6}
              gap={"8px"}
              cursor={isFree ? "pointer" : "default"}
              _hover={isFree ? { bg: "gray.50" } : undefined}
              onClick={() => {
                if (isFree) {
                  setSelectedChapter(idx);
                  setSelectedVideoUrl(video?.url || null);
                  setSelectedVideoId(video?.id || null);
                }
              }}
            >
              <Box>
                {isFree ? (
                  <VideoNewIcon color={isCompleted ? "#00A177" : "#40464B"} />
                ) : (
                  <Box
                    width="24px"
                    height="24px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="4px"
                    bg="#E0E0E0"
                    fontSize="14px"
                  >
                    🔒
                  </Box>
                )}
              </Box>

              <Flex align="center" gap="8px" flex={1}>
                <Text
                  fontSize="14px"
                  fontWeight={500}
                  color={isCompleted ? "#00A177" : "#2F2F34"}
                >
                  {video?.title}
                </Text>
                {isCompleted && <CompletedIcon />}
              </Flex>

              <Text fontSize="14px" color="#5C6670">
                {formatDuration(video?.duration || 0)}
              </Text>
            </Flex>
          );
        })}
      </Box>
    );
  };

  const renderChapters = () => {
    return (
      <Box>
        <Text fontWeight={600} fontSize="20px" color={"#2F2F34"} mb={4}>
          {t("courseDetail.chapters")}
        </Text>
        <Box
          bg="#CDD6DC33"
          borderRadius="lg"
          borderWidth={1}
          borderColor="#CDD6DC"
          overflow="hidden"
        >
          <Accordion allowToggle>
            {chapters?.map((chapter: any, idx: number) => (
              <AccordionItem
                key={chapter?.title || chapter?.id}
                borderBottom="1px solid"
                borderBottomColor="#CDD6DC"
                ref={idx === 0 ? chaptersHeaderRef : undefined}
              >
                <AccordionButton
                  px={6}
                  py={4}
                  bg="#CDD6DC33"
                  borderBottomWidth={1}
                  borderBottomColor="gray.100"
                >
                  <Flex flex={1} align="center" justify="space-between">
                    <Flex align="center" flex={1}>
                      <AccordionIcon mr={3} color="#5C6670" />
                      <Text fontSize="16px" fontWeight={600} color="#2F2F34">
                        {chapter?.title}
                      </Text>
                    </Flex>
                    <Text fontSize="14px" color="#5C6670">
                      {getChapterSummary(chapter)}
                    </Text>
                  </Flex>
                </AccordionButton>
                <AccordionPanel px={0} py={0} bg="white">
                  {renderChaptersVideo(chapter, idx)}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Box>
      </Box>
    );
  };

  return (
    <Box width={"100%"} overflowY={"auto"} pt={2} px={6} position="relative">
      <Button
        width={"fit-content"}
        variant="link"
        mb={4}
        onClick={() => navigate(-1)}
      >
        &larr; {t("courseDetail.goBack")}
      </Button>
      {renderVideoPlayer()}
      {renderHeader()}
      {/* Always show chapters for free courses - no enrollment needed */}
      {renderChapters()}

      {/* Restart Tour Button */}
      {!courseDetailTourGuide.isActive && (
        <RestartTourButton
          onClick={courseDetailTourGuide.startTour}
          position={{ bottom: "24px", right: "24px" }}
        />
      )}

      {/* Active Tour Guide */}
      {courseDetailTourGuide.isActive &&
        courseDetailTourGuide.currentStepData && (
          <TourGuide
            isActive={courseDetailTourGuide.isActive}
            currentStep={courseDetailTourGuide.currentStepData}
            currentStepIndex={courseDetailTourGuide.currentStep}
            totalSteps={courseDetailTourGuide.totalSteps}
            onNext={courseDetailTourGuide.nextStep}
            onBack={courseDetailTourGuide.previousStep}
            onClose={courseDetailTourGuide.stopTour}
            targetRef={
              courseDetailTourGuide.currentStepData.id === "enrollment-period"
                ? enrollmentPeriodRef
                : courseDetailTourGuide.currentStepData.id === "chapter-name"
                  ? chaptersHeaderRef
                  : undefined
            }
            borderRadius="8px"
          />
        )}
    </Box>
  );
};

export default FreeCourseDetail;
